import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { CFCProps, LOCATION_TYPES_CODE } from 'types';
import { RootState } from 'redux/root.reducer';

import {
  TextInput,
  Button,
  Loading,
  Link,
  Tag,
  Select,
  SelectItem,
  OverflowMenu,
  OverflowMenuItem,
  TagSkeleton
} from 'carbon-components-react';
import { FormError, CustomTooltipDefinition } from 'components';
import { Add16, ArrowRight16, Close16, Edit16 } from '@carbon/icons-react';
import { SkeletonText } from 'carbon-components-react';
import { pull, uniq } from 'lodash';
import WorkplaceLocationsCheckBoxes from './workplaceLocationsCheckboxes';

const stateListItems: { value: string | null; label: string }[] = [
  {
    label: 'ACT',
    value: 'ACT'
  },
  {
    label: 'NSW',
    value: 'NSW'
  },
  {
    label: 'NT',
    value: 'NT'
  },
  {
    label: 'QLD',
    value: 'QLD'
  },
  {
    label: 'SA',
    value: 'SA'
  },
  {
    label: 'TAS',
    value: 'TAS'
  },
  {
    label: 'VIC',
    value: 'VIC'
  },
  {
    label: 'WA',
    value: 'WA'
  },
  {
    label: 'Other',
    value: 'OT'
  }
];

const MapLocationForm: React.FC<Props> = (props: Props) => {
  const {
    user,
    appLoading,
    mLoading,
    lastEnquiry,
    submitting,
    shapeArea,
    address,
    orgListByBoundary,
    updateValues,
    values,
    touched: fTouched,
    errors: fErrors,
    setFieldTouched,
    setFieldError,
    handleSubmit,
    copyLastEnquiryHandler,
    applyEnquiryPresetsHandler,
    goEnquiryPresetsPageHandler,
    resetFormHandler,
    cancelEnquiryHandler
  } = props;

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPresetsPanel, setShowPresetsPanel] = useState<boolean>(false);
  const [hasEnquiryPresets, setHasEnquiryPresets] = useState<boolean>(false);
  const [noOfAssetOwners, setNoOfAssetOwners] = useState<number>();
  const addressLine1InputRef = useRef<HTMLInputElement>(null);

  // const setIsLocations = (value: boolean) => {
  //   isLocationsRef.current = value;
  // };

  const inputError = ((errors: any, touched: any) => (
    key:
      | 'addressLine1'
      | 'locality'
      | 'state'
      | 'postcode'
      | 'locationTypes'
      | 'locationsInRoad'
      | 'prLocationType'
      | 'rrLocationType'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );

  const fieldsChangeHandler = async (obj: any) => {
    const { key, data } = obj;
    // Validation
    if (['addressLine1', 'locality', 'postcode'].includes(key)) {
      values[key] = data.target.value;
      setFieldTouched(key, true);
    }

    if (key === 'state') {
      values.state = data;
      setFieldTouched(key, true);
    }

    if (key === 'locations') {
      let _locTypes: string[] = values.locationTypes;
      const sLocations: string[] = data.map((item: any) => item.value);

      values.locationsInRoad = sLocations.filter(
        (location: string) => location !== 'Private property'
      );

      if (values.locationsInRoad.length > 0) {
        values.rrLocationType = true;
        _locTypes?.push(LOCATION_TYPES_CODE.ROAD);
      } else {
        values.rrLocationType = false;
        _locTypes = pull(_locTypes, LOCATION_TYPES_CODE.ROAD);
      }

      if (sLocations.includes('Private property')) {
        values.prLocationType = true;
        _locTypes?.push(LOCATION_TYPES_CODE.PRIVATE);
      } else {
        values.prLocationType = false;
        _locTypes = pull(_locTypes, LOCATION_TYPES_CODE.PRIVATE);
      }
      values.locationTypes = uniq(_locTypes);
    }

    validateFields();
    updateValues(values);
  };

  // USECALLBACK
  const validateFields = useCallback(() => {
    const {
      addressLine1,
      locality,
      state,
      postcode,
      prLocationType,
      locationsInRoad
    } = values;
    const isInRoadLoc = locationsInRoad?.length > 0;
    let isValid = addressLine1 !== '' && (isInRoadLoc || prLocationType);

    if (!address?.locality && !locality) {
      isValid = false;
      setFieldError('locality', 'Suburb/Town/City is required');
    }

    if (!address?.state && !state) {
      isValid = false;
      setFieldError('state', 'State is required');
    }

    if (!address?.postcode && !postcode) {
      isValid = false;
      setFieldError('postcode', 'Postcode is required');
    } else {
      if (postcode) {
        if (postcode.length > 4) {
          isValid = false;
          setFieldError(
            'postcode',
            'Postcode must equal or less than 4 digits'
          );
        }
        if (
          Number.isNaN(Number(postcode)) ||
          !Number.isInteger(Number(postcode))
        ) {
          isValid = false;
          setFieldError('postcode', 'Postcode must be numeric');
        }
      }
    }

    setIsButtonDisabled(!isValid);
  }, [values, address, setFieldError]);

  const handleClickUsePresets = (hasEnquiryPresets: boolean) => {
    hasEnquiryPresets
      ? applyEnquiryPresetsHandler()
      : setShowPresetsPanel(true);
  };

  // USEEFFECTS
  useEffect(() => {
    if (orgListByBoundary?.length > 0) {
      setNoOfAssetOwners(orgListByBoundary.length);
    } else {
      setNoOfAssetOwners(undefined);
    }
  }, [orgListByBoundary]);

  useEffect(() => {
    validateFields();
  }, [values, validateFields]);

  useEffect(() => {
    appLoading || (submitting && setIsLoading(submitting));
  }, [appLoading, submitting]);

  useEffect(() => {
    setHasEnquiryPresets(
      user?.preferences?.enquiryPresets !== undefined &&
        user?.preferences?.enquiryPresets !== null
    );
  }, [user]);

  useEffect(() => {
    if (addressLine1InputRef.current) {
      addressLine1InputRef.current.focus();
    }
  }, [address]);

  return isLoading ? (
    <div className="loader--wrapper">
      <Loading description="Active loading indicator" withOverlay={false} />
    </div>
  ) : (
    <>
      <form
        onSubmit={handleSubmit}
        className={`lodgeEnquiryFrom custom-scroller`}
        noValidate
      >
        <div className="data-entry-buttons-container">
          <Button
            className="copy-last-job-btn"
            kind="ghost"
            size="small"
            data-testid="copy-last-job-btn"
            disabled={!lastEnquiry}
            onClick={() => copyLastEnquiryHandler()}
          >
            {'Copy from last job'}
          </Button>
          <div className="presets-container">
            <Button
              className="presets-btn"
              kind="ghost"
              size="small"
              data-testid="presets-btn"
              onClick={() => handleClickUsePresets(hasEnquiryPresets)}
            >
              Use presets
            </Button>
            <OverflowMenu light flipped size="sm">
              <OverflowMenuItem
                itemText="Edit presets"
                onClick={() => goEnquiryPresetsPageHandler()}
              />
              <OverflowMenuItem
                itemText="Reset form"
                onClick={() => resetFormHandler()}
              />
            </OverflowMenu>
          </div>
        </div>
        {showPresetsPanel && (
          <div className="presets-empty-container">
            <div className="presets-empty-header">
              <div className="presets-empty-header--text">
                Presets save time when lodging your enquiries
              </div>
              <div className="presets-empty-header--close">
                <button
                  className="bx--modal-close"
                  type="button"
                  title="Close"
                  aria-label="Close"
                  onClick={() => setShowPresetsPanel(false)}
                >
                  <Close16 />
                </button>
              </div>
            </div>
            <div className="presets-empty-body">
              <Button
                className="create-presets-btn"
                renderIcon={Add16}
                kind="tertiary"
                onClick={() => goEnquiryPresetsPageHandler()}
              >
                Create presets
              </Button>
            </div>
          </div>
        )}
        <div className="lodgeEnquiryFrom--body">
          {/* Approximate map location address */}
          <section className="formFieldItem address--wrapper">
            <div className="address--body-top">
              <CustomTooltipDefinition
                tooltipText="The approximate address is in the middle of your shape. You can edit the street address to better match the job site location if necessary."
                className="address--label"
              >
                Approximate address
              </CustomTooltipDefinition>
              <Link
                className="address--btn-drawAgain"
                href="#"
                onClick={() => cancelEnquiryHandler(false)}
              >
                Draw again
              </Link>
            </div>
            <div className="address--body">
              {address ? (
                <div className="bx--form-item bx--text-input-wrapper">
                  <div className="bx--text-input__field-outer-wrapper">
                    <div className="bx--text-input__field-wrapper">
                      <input
                        ref={addressLine1InputRef}
                        id="addressLine1"
                        placeholder="Address Line1"
                        type="text"
                        className="bx--text-input bx--text__input address-line-1"
                        title="Address Line1"
                        name="addressLine1"
                        data-testid="addressLine1"
                        onBlur={validateFields}
                        onChange={(value: any) =>
                          fieldsChangeHandler({
                            key: 'addressLine1',
                            data: value
                          })
                        }
                        value={values.addressLine1 || ''}
                      />
                      <Edit16
                        className="bx--date-picker__icon address-line-1"
                        onClick={() => addressLine1InputRef.current?.focus()}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <SkeletonText />
              )}
              {inputError('addressLine1')}
              {address ? (
                <>
                  {address?.locality ? (
                    <div className="address--content">{address?.locality}</div>
                  ) : (
                    <>
                      <TextInput
                        id="locality"
                        name="locality"
                        hideLabel={true}
                        labelText=""
                        placeholder="Suburb/Town/City"
                        data-testid="locality"
                        onBlur={validateFields}
                        onChange={(value: any) =>
                          fieldsChangeHandler({ key: 'locality', data: value })
                        }
                        defaultValue={values.locality}
                      />
                      {inputError('locality')}
                    </>
                  )}
                  <div className="section--divider"></div>
                  {address?.state ? (
                    <div className="address--content sub-content">
                      {address.state}
                    </div>
                  ) : (
                    <div className="address--short-input">
                      <Select
                        id="state"
                        name="state"
                        placeholder="State"
                        className="sub-content"
                        hideLabel={true}
                        value={values.state}
                        onChange={(evt) =>
                          fieldsChangeHandler({
                            key: 'state',
                            data: evt.target.value
                          })
                        }
                        onBlur={validateFields}
                      >
                        <SelectItem text="" value="" />
                        {stateListItems.map((item: any, index: number) => {
                          return (
                            <SelectItem
                              key={`state-${index}`}
                              text={item.label}
                              value={item.value}
                            />
                          );
                        })}
                      </Select>
                      {inputError('state')}
                    </div>
                  )}
                  {address?.postcode ? (
                    <div className="address--content sub-content">
                      {address?.postcode}
                    </div>
                  ) : (
                    <div className="address--short-input">
                      <TextInput
                        id="postcode"
                        name="postcode"
                        placeholder="Postcode"
                        hideLabel={true}
                        labelText=""
                        data-testid="postcode"
                        className="sub-content"
                        onBlur={validateFields}
                        onChange={(value: any) =>
                          fieldsChangeHandler({ key: 'postcode', data: value })
                        }
                        defaultValue={values.postcode}
                      />
                      {inputError('postcode')}
                    </div>
                  )}
                  <div className="section--divider"></div>
                </>
              ) : (
                <SkeletonText />
              )}
            </div>
            <div className="address--legends">
              {shapeArea ? (
                <Tag type="blue" title="Area cover">
                  {shapeArea > 0.01
                    ? `${shapeArea.toFixed(2)} km²`
                    : `${Number(shapeArea * 1e6).toFixed(2)} m²`}
                </Tag>
              ) : (
                <TagSkeleton />
              )}
              {!mLoading && noOfAssetOwners && (
                <Tag type="purple" title="Asset owner">
                  {`${noOfAssetOwners} Asset owners`}
                </Tag>
              )}
              {mLoading && <TagSkeleton />}
            </div>
          </section>
          {/* Workplace location(s) */}
          <section className="formFieldItem">
            <span className="bx--label">Workplace location(s)</span>
            <WorkplaceLocationsCheckBoxes
              formValues={{
                locationTypes: values.locationTypes,
                locationsInRoad: values.locationsInRoad
              }}
              onChange={(value) => {
                fieldsChangeHandler({
                  key: 'locations',
                  data: value
                });
              }}
            />
            {inputError('locationsInRoad')}
          </section>
        </div>
        <div className="progressBar--wrapper">
          <div className="progressBar completed"></div>
          <div className="progressBar"></div>
          <div className="progressBar"></div>
        </div>
        <section className="button--wrapper">
          <Button
            className="submitForm--fullButton"
            type="submit"
            kind={!fErrors ? 'danger' : 'primary'}
            renderIcon={ArrowRight16}
            data-testid="submit"
            disabled={isButtonDisabled || submitting}
          >
            {'Continue'}
          </Button>
        </section>
      </form>
    </>
  );
};

MapLocationForm.displayName = 'MapLocationForm';
const mapStateToProps = ({
  app,
  session,
  lodgeEnquiryCache,
  enquiresCache,
  membersCache
}: RootState) => ({
  user: session.currentUser,
  appLoading: app?.loading,
  mLoading: membersCache?.loading,
  lastEnquiry: lodgeEnquiryCache?.lastEnquiry,
  submitting: lodgeEnquiryCache?.submitting,
  shapeArea:
    lodgeEnquiryCache?.shapeArea ||
    enquiresCache?.data?.enquiry?.shapeArea / 1000000,
  address: lodgeEnquiryCache?.address || enquiresCache?.data?.enquiry?.Address,
  orgListByBoundary: membersCache?.orgListByBoundary
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector> & any;

export default connector(MapLocationForm);
